import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent } from 'react-native';

import { LineSeparator } from '@/components/LineSeparator';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useActiveEventCount, useActiveSelectionCount } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { betPayoutSelector } from '@/feature/betslip-sbk/utils/betslip-selectors';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { useIsCashCurrency } from '@/hooks/use-social-currency';

type EntrySummaryItemParlayProps = {
    betId: string;
    columnWidths: { entry: number; payout: number };
    createLayoutHandler: (column: 'entry' | 'payout') => (event: LayoutChangeEvent) => void;
};

export const EntrySummaryItemParlay = ({ betId, columnWidths, createLayoutHandler }: EntrySummaryItemParlayProps) => {
    const { t } = useTranslation('betslip_sbk');
    const { formatCurrency } = useFormatCurrency();

    const activeSelectionCount = useActiveSelectionCount();
    const activeEventCount = useActiveEventCount();

    const stake = useSbkBetSlipStore(state => state.bets[betId]?.stake ?? 0);
    const isCashCurrency = useIsCashCurrency();
    const payout = useSbkBetSlipStore(betPayoutSelector(betId, isCashCurrency));

    return (
        <Box height={80}>
            <Row py="s16" alignItems="center" justifyContent="space-between">
                <Box flex={1} pr="s16" gap="s4">
                    <Text variant="bodySmall" color="neutral300" numberOfLines={1}>
                        {t('events_count', { count: activeEventCount })}
                    </Text>
                    <Text variant="titleMedium" color={ds.colorToken.text.primary} numberOfLines={1}>
                        {t('parlay_pick', { count: activeSelectionCount })}
                    </Text>
                </Box>
                <Box minWidth={columnWidths.entry} pr="s16">
                    <Row alignItems="center" justifyContent="flex-end" gap="s4" onLayout={createLayoutHandler('entry')}>
                        <SocialCurrencyActiveIcon />
                        <Text variant="bodyMedium" color={ds.colorToken.text.primary}>
                            {formatCurrency(stake)}
                        </Text>
                    </Row>
                </Box>
                <Box minWidth={columnWidths.payout}>
                    <Row
                        alignItems="center"
                        justifyContent="flex-end"
                        gap="s4"
                        onLayout={createLayoutHandler('payout')}
                    >
                        <SocialCurrencyActiveIcon />
                        <Text variant="bodyMedium" color={ds.colorToken.text.primary}>
                            {formatCurrency(payout)}
                        </Text>
                    </Row>
                </Box>
            </Row>
            <LineSeparator />
        </Box>
    );
};
